import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GetCurrentSessionResult } from '@core/model/common';
import { segundoFatorSatisfeito } from '@model/two-factor-current-method.model';
import { AuthService, TenancyService } from '@core/services/common';
import { MatIcon } from '@angular/material/icon';
import { Modules } from '@enum/central/modules.enum';
import { PlanCapabilitiesService, planoIncluiModulo } from '@shared/services/plan-capabilities.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

/**
 * O menu de usuário — o bloco de perfil no RODAPÉ do menu lateral.
 *
 * Nasceu na barra superior (substituindo `kt-user-profile`) e MUDOU DE LUGAR em
 * 01/09/2026, na revisão do dono: o nome do usuário aparecia nos dois lugares
 * ao mesmo tempo, e o bloco do menu lateral — que tinha cara de clicável — era
 * `div` inerte. As duas referências resolvem isso no menu lateral, então a
 * barra superior perdeu o nome e este componente ganhou o avatar e o e-mail.
 *
 * O antigo abria por `ngbDropdown` (ng-bootstrap, que sai na C3 item 6) e
 * assinava `currentUserObserver` sem nunca desfazer a inscrição. Aqui o painel
 * é `mat-menu` — overlay do CDK — e a inscrição é encerrada no `ngOnDestroy`.
 *
 * Enquanto o método de 2FA ainda não carregou, Meu perfil continua disponível,
 * mas leva à aba Segurança; as operações pessoais seguem protegidas até a
 * sessão satisfazer o segundo fator.
 */
@Component({
  selector: 'mg-menu-usuario',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, AvatarComponent, MatMenuTrigger, MatMenu, MatMenuItem],
})
export class UserMenuComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly tenancy = inject(TenancyService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly plano = inject(PlanCapabilitiesService);


  sessao: GetCurrentSessionResult = null;

  private readonly inscricoes = new Subscription();

  ngOnInit(): void {
    this.inscricoes.add(
      this.authService.currentUserObserver.subscribe(sessao => {
        this.sessao = sessao;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.inscricoes.unsubscribe();
  }

  /**
   * Só uma sessão com o segundo fator SATISFEITO mexe na própria conta.
   *
   * SEGUNDO FATOR OPT-IN (decisão do dono, 02/09/2026): a leitura era
   * `is_verified === true`, ou seja "tem 2FA cadastrado". Com o 2FA opcional
   * isso vira uma imposição de cadastro — quem nunca cadastrou fator perderia
   * "Meu Perfil" e "Alterar Senha" para sempre, enquanto a API (que gateia as
   * duas rotas pelo `two_factor`) passou a aceitá-las. O predicado correto é o
   * mesmo do guard: passou pelo segundo fator OU não há segundo fator a passar.
   *
   * Com o método ainda não carregado (`null`), este predicado nega as ações
   * protegidas. O único link de perfil continua visível e leva à Segurança.
   */
  get podeEditarUsuario(): boolean {
    return segundoFatorSatisfeito(this.authService.twoFactorMethod);
  }

  perfil(): void {
    const destino = this.podeEditarUsuario ? '/perfil' : '/perfil/seguranca';
    void this.router.navigateByUrl(`${this.tenancy.basePath}${destino}`);
  }

  /** Assinatura é do tenant: o central não tem a tela nem a rota. */
  get ehTenant(): boolean {
    return this.tenancy.isSubdomain;
  }

  minhaAssinatura(): void {
    void this.router.navigateByUrl(`${this.tenancy.basePath}/configuracoes/assinatura`);
  }

  /** O MCP é capacidade do plano; sem o módulo a tela e a API recusam, então o item some. */
  get assistenteIncluido(): boolean {
    return planoIncluiModulo(this.plano.modulos, Modules.AI_ASSISTANT);
  }

  mcp(): void {
    void this.router.navigateByUrl(`${this.tenancy.basePath}/mcp`);
  }

  sair(): void {
    this.inscricoes.add(this.authService.onLogoutHandler().subscribe(() => window.location.reload()));
  }
}
